import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { Phone, Mail, MessageSquare, X, Star, Building2, Navigation, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { ccCard, Avatar } from "./ui";
import { CAREGIVER_GEO, MAP_CENTER } from "../modules/opsData";
import { haversineMiles, getDeviceLocation } from "../lib/location";

const pinIcon = (color, glyph) =>
  L.divIcon({
    className: "",
    html: `<div style="width:34px;height:34px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(55,65,81,.25);display:flex;align-items:center;justify-content:center;color:#374151;font-size:15px;font-weight:800">${glyph}</div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });

// Gold star pin for featured daycare listings
const featuredPin = L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:50%;background:#F5C445;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.25);display:flex;align-items:center;justify-content:center;color:#7C5E10;font-weight:900;font-size:13px">★</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
  popupAnchor: [0, -14],
});

// Fire-and-forget featured-listing analytics
const trackFeatured = (id, type) =>
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/featured-daycares/${id}/track`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type }),
  }).catch(() => {});

// Keeps the Leaflet canvas sized correctly when its container resizes
const MapResizer = () => {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer().parentElement;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      try {
        const c = map.getContainer();
        if (c.clientWidth > 0 && c.clientHeight > 0) map.invalidateSize();
      } catch {
        /* zero-size canvas during layout shifts */
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

const HeatLayer = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    const layer = L.heatLayer(points, { radius: 38, blur: 28, maxZoom: 12, gradient: { 0.3: "#B3E0F2", 0.6: "#F8C8DC", 1: "#E5A0BC" } });
    layer.addTo(map);
    return () => map.removeLayer(layer);
  }, [map, points]);
  return null;
};

const Recenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo([center.lat, center.lng], 13);
  }, [map, center]);
  return null;
};

// Auto-fit the map to show every pin once data loads
const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    try {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 6 });
    } catch {
      /* ignore fit errors during layout */
    }
  }, [points, map]);
  return null;
};

export default function CareMap({ ctx }) {
  const [selected, setSelected] = useState(null);
  const [showHeat, setShowHeat] = useState(true);
  const [userLoc, setUserLoc] = useState(null);
  const [locating, setLocating] = useState(false);
  const [realDaycares, setRealDaycares] = useState([]);
  const [featured, setFeatured] = useState([]);
  const activeCaregivers = ctx.caregivers.filter((c) => c.status === "active");

  // Live facility + featured daycare pins — real listings only, no demo markers
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/care-map`)
      .then((r) => (r.ok ? r.json() : { facilities: [], featured: [] }))
      .then((d) => {
        setRealDaycares(d.facilities || []);
        setFeatured(d.featured || []);
      })
      .catch(() => {});
  }, []);

  const locateMe = async () => {
    setLocating(true);
    try {
      setUserLoc(await getDeviceLocation());
      toast.success("Location found — showing who's nearest to you.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLocating(false);
    }
  };

  const withDistance = (list, geoLookup) =>
    list
      .map((item) => {
        const g = geoLookup(item);
        return userLoc && g ? { ...item, realDist: haversineMiles(userLoc.lat, userLoc.lng, g.lat, g.lng) } : item;
      })
      .sort((a, b) => (a.realDist ?? 999) - (b.realDist ?? 999));

  const heatPoints = [
    ...activeCaregivers
      .map((c) => ({ c, g: CAREGIVER_GEO[c.id] }))
      .filter((x) => x.g)
      .map((x) => [x.g.lat, x.g.lng, x.c.available ? 0.9 : 0.4]),
    ...realDaycares.map((d) => [d.lat, d.lng, 0.7]),
    ...featured.map((f) => [f.lat, f.lng, 1.1]),
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="care-map-view">
      <div className={`${ccCard} overflow-hidden lg:col-span-2`}>
        <div className="p-4 flex items-center justify-between flex-wrap gap-2 border-b border-[#FCE6E9] dark:border-[#374457]">
          <div>
            <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100">Caregiver & Daycare Discovery Map</h3>
            <p className="text-[11px] text-[#9CA3AF] font-medium">{activeCaregivers.length} live caregivers · {realDaycares.length} daycares near you</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={locateMe}
              disabled={locating}
              data-testid="map-near-me-btn"
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] border border-[#B3E0F2] text-[#374151] rounded-full text-[11px] font-bold transition-all disabled:opacity-50"
            >
              <LocateFixed className="w-3.5 h-3.5" /> {locating ? "Locating…" : userLoc ? "Located ✓" : "Near Me"}
            </button>
            <button
              onClick={() => setShowHeat((h) => !h)}
              data-testid="map-heat-toggle"
            className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold border transition-all ${
              showHeat ? "bg-[#F8C8DC] border-[#EAAFC5] text-[#374151]" : "bg-[#FFF9F2] border-[#FCE6E9] text-[#9CA3AF] dark:bg-[#273244] dark:border-[#374457]"
            }`}
          >
            {showHeat ? "Heatmap On" : "Heatmap Off"}
            </button>
          </div>
        </div>
        <div className="h-80 sm:h-[420px] lg:h-[480px] relative z-0 isolate" data-testid="map-container">
          <MapContainer center={MAP_CENTER} zoom={12} style={{ height: "100%", width: "100%", position: "relative", zIndex: 0 }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {showHeat && <HeatLayer points={heatPoints} />}
            <Recenter center={userLoc} />
            <FitBounds points={[...featured.map((f) => [f.lat, f.lng]), ...realDaycares.map((d) => [d.lat, d.lng])]} />
            {userLoc && <Marker position={[userLoc.lat, userLoc.lng]} icon={pinIcon("#F5C445", "★")} />}
            {activeCaregivers.map((c) => {
              const g = CAREGIVER_GEO[c.id];
              if (!g) return null;
              return (
                <Marker
                  key={c.id}
                  position={[g.lat, g.lng]}
                  icon={pinIcon(c.available ? "#B3E0F2" : "#E5E7EB", c.name.charAt(0))}
                  eventHandlers={{ click: () => setSelected({ type: "caregiver", data: c }) }}
                />
              );
            })}
            {realDaycares.map((d) => (
              <Marker
                key={d.id}
                position={[d.lat, d.lng]}
                icon={pinIcon("#F8C8DC", "🏠")}
                eventHandlers={{ click: () => setSelected({ type: "daycare", data: d }) }}
              />
            ))}
            {featured.map((f) => (
              <Marker key={f.id} position={[f.lat, f.lng]} icon={featuredPin} eventHandlers={{ click: () => trackFeatured(f.id, "view") }}>
                <Popup>
                  <div style={{ minWidth: 170 }} data-testid={`map-featured-popup-${f.id}`}>
                    <p style={{ fontWeight: 800, margin: "0 0 2px", fontSize: 13 }}>{f.name}</p>
                    <p style={{ margin: "0 0 4px", fontSize: 11, color: "#6B7280" }}>{[f.city, f.state].filter(Boolean).join(", ")}</p>
                    {f.description && <p style={{ margin: "0 0 6px", fontSize: 11, color: "#6B7280" }}>{f.description.slice(0, 120)}</p>}
                    {f.enroll_url && (
                      <a href={f.enroll_url} target="_blank" rel="noreferrer" onClick={() => trackFeatured(f.id, "click")} style={{ fontSize: 11, fontWeight: 800, color: "#B2607F" }}>
                        Enroll / Visit →
                      </a>
                    )}
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([f.name, f.city, f.state].filter(Boolean).join(" "))}`} target="_blank" rel="noreferrer" data-testid={`map-gmaps-${f.id}`} style={{ fontSize: 11, fontWeight: 800, color: "#3D7E9E", display: "block", marginTop: 3 }}>
                      Google Maps profile →
                    </a>
                  </div>
                </Popup>
              </Marker>
            ))}
            <MapResizer />
          </MapContainer>
        </div>
      </div>

      {/* Drawer profile */}
      <div className={`${ccCard} p-6 h-fit lg:sticky lg:top-32`} data-testid="map-drawer">
        {!selected ? (
          <div className="text-center py-10">
            <span className="w-14 h-14 rounded-full bg-[#E6F2FA] flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-6 h-6 text-[#5CB3DC]" />
            </span>
            <p className="text-xs text-[#9CA3AF] font-medium" data-testid="map-drawer-empty">
              Click a pin to view the full profile and instant contact options.
            </p>
            <div className="mt-6 space-y-2 text-left">
              <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">Legend</p>
              <p className="text-xs font-semibold text-[#6B7280] flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full bg-[#B3E0F2] border-2 border-white shadow" /> Available caregiver
              </p>
              <p className="text-xs font-semibold text-[#6B7280] flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full bg-[#E5E7EB] border-2 border-white shadow" /> Booked caregiver
              </p>
              <p className="text-xs font-semibold text-[#6B7280] flex items-center gap-2">
                <span className="w-3.5 h-3.5 rounded-full bg-[#F8C8DC] border-2 border-white shadow" /> Daycare facility
              </p>
            </div>
            {userLoc && (
              <div className="mt-6 space-y-2 text-left" data-testid="map-nearest-list">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">Nearest to you</p>
                {withDistance(activeCaregivers, (c) => CAREGIVER_GEO[c.id]).slice(0, 3).map((c) => (
                  <button key={c.id} onClick={() => setSelected({ type: "caregiver", data: c })} className="w-full flex items-center justify-between p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl hover:border-[#B3E0F2] transition-colors" data-testid={`map-nearest-cg-${c.id}`}>
                    <span className="text-xs font-extrabold text-[#374151]">{c.name}</span>
                    <span className="text-[10px] font-bold text-[#5CB3DC]">{c.realDist?.toFixed(1)} mi</span>
                  </button>
                ))}
                {withDistance(realDaycares, (d) => d).slice(0, 2).map((d) => (
                  <button key={d.id} onClick={() => setSelected({ type: "daycare", data: d })} className="w-full flex items-center justify-between p-2.5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl hover:border-[#F8C8DC] transition-colors" data-testid={`map-nearest-dc-${d.id}`}>
                    <span className="text-xs font-extrabold text-[#374151]">{d.name}</span>
                    <span className="text-[10px] font-bold text-[#E5A0BC]">{d.realDist?.toFixed(1)} mi</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : selected.type === "caregiver" ? (
          <div data-testid="map-drawer-caregiver">
            <div className="flex justify-between items-start mb-4">
              <Avatar src={selected.data.photo} name={selected.data.name} size="w-16 h-16" />
              <button onClick={() => setSelected(null)} className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9]" data-testid="map-drawer-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">{selected.data.name}</h3>
            <p className="text-xs text-[#9CA3AF] font-medium">{selected.data.title}</p>
            <div className="flex items-center gap-1.5 mt-2">
              <Star className="w-3.5 h-3.5 text-[#F5C445] fill-[#F5C445]" />
              <span className="text-xs font-bold text-[#374151] dark:text-slate-200">{selected.data.rating > 0 ? selected.data.rating : "New"}</span>
              <span className="text-[11px] text-[#9CA3AF]">({selected.data.reviewCount} reviews) · {(() => { const g = CAREGIVER_GEO[selected.data.id]; return userLoc && g ? `${haversineMiles(userLoc.lat, userLoc.lng, g.lat, g.lng).toFixed(1)} mi from you` : `${selected.data.distanceMiles} mi`; })()}</span>
            </div>
            <div className="flex gap-1.5 mt-3 flex-wrap">
              {selected.data.services.slice(0, 4).map((s) => (
                <span key={s} className="px-2 py-0.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-bold rounded-full">
                  {s}
                </span>
              ))}
            </div>
            <p className="text-sm font-extrabold text-[#374151] mt-3 dark:text-slate-100">
              ${selected.data.hourlyRate}–${selected.data.rateMax}/hr
            </p>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <a href="tel:+15550142000" className="flex flex-col items-center gap-1 p-2.5 bg-[#E2F4E1] rounded-2xl text-[#2F6031] text-[10px] font-extrabold hover:bg-[#D2EBD1] transition-colors" data-testid="map-contact-call">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href="mailto:care@nesttrack.app" className="flex flex-col items-center gap-1 p-2.5 bg-[#E6F2FA] rounded-2xl text-[#3D7E9E] text-[10px] font-extrabold hover:bg-[#D6EBF7] transition-colors" data-testid="map-contact-email">
                <Mail className="w-4 h-4" /> Email
              </a>
              <a href="sms:+15550142000" className="flex flex-col items-center gap-1 p-2.5 bg-[#FCE6E9] rounded-2xl text-[#B2607F] text-[10px] font-extrabold hover:bg-[#F8C8DC] transition-colors" data-testid="map-contact-text">
                <MessageSquare className="w-4 h-4" /> Text
              </a>
            </div>
            <button
              onClick={() => ctx.viewCaregiver(selected.data.id)}
              className="w-full mt-3 px-4 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-xs font-bold transition-all duration-200"
              data-testid="map-view-full-profile"
            >
              View Full Profile
            </button>
            {CAREGIVER_GEO[selected.data.id] && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${CAREGIVER_GEO[selected.data.id].lat},${CAREGIVER_GEO[selected.data.id].lng}`}
                target="_blank"
                rel="noreferrer"
                data-testid="map-directions-btn"
                className="w-full mt-2 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#E2F4E1] hover:bg-[#D2EBD1] text-[#2F6031] rounded-full text-xs font-bold transition-colors"
              >
                <Navigation className="w-3.5 h-3.5" /> Take Me To Location
              </a>
            )}
          </div>
        ) : (
          <div data-testid="map-drawer-daycare">
            <div className="flex justify-between items-start mb-4">
              <span className="w-14 h-14 rounded-full bg-[#FCE6E9] flex items-center justify-center text-xl">🏠</span>
              <button onClick={() => setSelected(null)} className="w-7 h-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9]" data-testid="map-drawer-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">{selected.data.name}</h3>
            <p className="text-xs text-[#9CA3AF] font-medium mt-1">Licensed facility{selected.data.capacity ? ` · Capacity ${selected.data.capacity}` : ""}</p>
            <a href="tel:+15550192834" className="w-full mt-4 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#E2F4E1] hover:bg-[#D2EBD1] text-[#2F6031] rounded-full text-xs font-bold transition-colors" data-testid="map-daycare-call">
              <Phone className="w-4 h-4" /> Call Facility
            </a>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${selected.data.lat},${selected.data.lng}`}
              target="_blank"
              rel="noreferrer"
              data-testid="map-daycare-directions"
              className="w-full mt-2 flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#E6F2FA] hover:bg-[#D6EBF7] text-[#3D7E9E] rounded-full text-xs font-bold transition-colors"
            >
              <Navigation className="w-3.5 h-3.5" /> Take Me To Location
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
